import { useState } from "react";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/lib/i18n";

const toIso = (d) => format(d, "yyyy-MM-dd");
const fromIso = (s) => (s ? new Date(`${s}T00:00:00`) : undefined);

const Legend = ({ t }) => (
  <div className="flex gap-5 px-4 pb-4 text-xs text-slate-400" data-testid="calendar-legend">
    <span className="flex items-center gap-2"><span className="w-3 h-3 border border-[#D4AF37]/60" />{t.cal_free}</span>
    <span className="flex items-center gap-2"><span className="w-3 h-3 bg-white/10" />{t.cal_booked}</span>
  </div>
);

export default function DatePicker({ value, onChange, booked }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const locale = lang === "fr" ? fr : enUS;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const bookedDays = booked.map(fromIso);
  const selected = fromIso(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="booking-date-input"
          className="w-full flex items-center justify-between border-b border-white/20 py-3 text-base text-left hover:border-[#D4AF37] focus:border-[#D4AF37] outline-none transition-colors duration-300">
          <span className={selected ? "" : "text-slate-500"}>{selected ? format(selected, "EEEE d MMMM yyyy", { locale }) : t.f_pick_date}</span>
          <CalendarDays className="w-4 h-4 text-[#D4AF37]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 bg-[#0A1325] border-[#D4AF37]/40 rounded-none" data-testid="booking-calendar">
        <Calendar
          mode="single"
          locale={locale}
          selected={selected}
          defaultMonth={selected || today}
          fromDate={today}
          disabled={[{ before: today }, ...bookedDays]}
          modifiers={{ booked: bookedDays }}
          modifiersClassNames={{ booked: "!bg-white/10 !text-slate-500 line-through !opacity-100" }}
          onSelect={(d) => { if (d) { onChange(toIso(d)); setOpen(false); } }}
          classNames={{ day_selected: "bg-[#D4AF37] text-[#050B14] hover:bg-[#D4AF37] focus:bg-[#D4AF37]", day_today: "border border-[#D4AF37]/50" }}
        />
        <Legend t={t} />
      </PopoverContent>
    </Popover>
  );
}
